import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  googleAuth: (credential) => api.post('/auth/google', { credential }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Generations
export const generationsAPI = {
  create: (data) => api.post('/generations', data),
  list: (page = 1, perPage = 20) =>
    api.get(`/generations?page=${page}&per_page=${perPage}`),
  get: (id) => api.get(`/generations/${id}`),
  getOptions: () => api.get('/generations/options/types'),
};

// Upload
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadReference: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/reference', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Credits
export const creditsAPI = {
  getBalance: () => api.get('/credits/balance'),
  getPacks: () => api.get('/credits/packs'),
  purchase: (packId) => api.post('/credits/purchase', { credit_pack_id: packId }),
  getTransactions: (page = 1, perPage = 20) =>
    api.get(`/credits/transactions?page=${page}&per_page=${perPage}`),
};

// Memberships
export const membershipsAPI = {
  getPlans: () => api.get('/memberships/plans'),
  getCurrent: () => api.get('/memberships/current'),
  subscribe: (planId, isYearly = false) =>
    api.post('/memberships/subscribe', { plan_id: planId, is_yearly: isYearly }),
  cancel: (membershipId) => api.post(`/memberships/${membershipId}/cancel`),
  getHistory: () => api.get('/memberships/history'),
};

// Admin
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),

  // Users
  listUsers: (page = 1, perPage = 50, search = '') =>
    api.get(`/admin/users?page=${page}&per_page=${perPage}&search=${search}`),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  addCredits: (userId, amount, description) =>
    api.post(`/admin/users/${userId}/credits`, { amount, description }),

  // Generations
  listGenerations: (page = 1, perPage = 50, userId = null) => {
    let url = `/admin/generations?page=${page}&per_page=${perPage}`;
    if (userId) url += `&user_id=${userId}`;
    return api.get(url);
  },

  // Credit Packs
  listCreditPacks: () => api.get('/admin/credit-packs'),
  createCreditPack: (data) => api.post('/admin/credit-packs', data),
  updateCreditPack: (id, data) => api.patch(`/admin/credit-packs/${id}`, data),
  deleteCreditPack: (id) => api.delete(`/admin/credit-packs/${id}`),

  // Membership Plans
  listMembershipPlans: () => api.get('/admin/membership-plans'),
  createMembershipPlan: (data) => api.post('/admin/membership-plans', data),
  updateMembershipPlan: (id, data) => api.patch(`/admin/membership-plans/${id}`, data),
  deleteMembershipPlan: (id) => api.delete(`/admin/membership-plans/${id}`),

  // Transactions
  listTransactions: (page = 1, perPage = 50, typeFilter = null) => {
    let url = `/admin/transactions?page=${page}&per_page=${perPage}`;
    if (typeFilter) url += `&type_filter=${typeFilter}`;
    return api.get(url);
  },

  // Memberships (active subscriptions)
  listMemberships: (page = 1, perPage = 50, statusFilter = null) => {
    let url = `/admin/memberships?page=${page}&per_page=${perPage}`;
    if (statusFilter) url += `&status_filter=${statusFilter}`;
    return api.get(url);
  },
};

export default api;
